import type { KidSession } from '../api/auth'

/**
 * The signed-in child — OD-52's credential, held the way `device.ts` holds the
 * registered one.
 *
 * 🔴 A MODULE, NOT A REACT CONTEXT. The same shape and the same reason as
 * `device.ts` and `parentSession.ts`: the token is read by the outbox from a
 * 30-second timer and a `window.online` handler, neither of which is inside
 * React's lifecycle.
 *
 * 🔴 IT SHADOWS `device.ts` RATHER THAN REPLACING IT, AND THAT IS THE WHOLE
 * INTEGRATION DECISION.
 *
 * `ensureDevice()` is still the boot path for a tablet nobody has rostered — a
 * school that has not run a roster import must keep working, and `deviceId` is
 * still what `Device` rows are keyed by. What changes is *whose* token the API
 * layer sends: when a child has signed in, theirs. `deviceToken()` below is the
 * one function that resolves that precedence, so no screen has to.
 *
 * ⚠️ WHICH ALSO FIXES THE SHARED-DEVICE LIMIT `kidProgress.ts` REPORTS. Its
 * docblock says *"this record diverges on a shared device — two children on one
 * laptop share it"*, and names the teacher's Play Mode handoff as the product's
 * answer. A sign-in is a second answer: the child names themselves, so their
 * progress can be namespaced by `playerId` rather than by the tablet. 📋 That
 * namespacing is NOT done here — `kidProgress.ts` is still keyed by device, and
 * changing it is a data-migration decision about progress already on tablets.
 * Reported rather than half-made.
 */

const SESSION_KEY = 'kalqy.kid'

/** What a screen needs to greet the child and scope their play. */
export interface KidSessionState {
  playerId: string
  deviceToken: string
  name: string
  avatar: string | null
  ageBand: string
  classId: string
  className: string
  /** The school the class belongs to. Drawn on the sign-in card — `knownClasses.ts`. */
  schoolName: string
}

/**
 * Minimal `localStorage` shape, injectable so the tests (and Node) do not need
 * a DOM, and so a browser that throws on access cannot take the boot path down.
 * Mirrors `parentSession.ts`'s shim deliberately — two storage shims with
 * different semantics is how one of them ends up untested.
 */
export interface SessionStorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStorage(): SessionStorageLike | null {
  try {
    return (globalThis as { localStorage?: SessionStorageLike }).localStorage ?? null
  } catch {
    return null
  }
}

let storage: SessionStorageLike | null = defaultStorage()

/** Test seam. Also the escape hatch if a browser's storage throws. */
export function setKidSessionStorage(next: SessionStorageLike | null): void {
  storage = next
}

/**
 * 🔴 ALL FIELDS OR NONE — the rule `parentSession.ts` states and for a sharper
 * reason here. A half-cache would be a child the app believes is signed in with
 * no token to prove it: every write 401s, the outbox retries forever, and the
 * only escape from a four-year-old's point of view is that the game stopped
 * working.
 */
export function getKidSession(): KidSessionState | null {
  let raw: string | null
  try {
    raw = storage?.getItem(SESSION_KEY) ?? null
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const s = parsed as Partial<KidSessionState>
    if (typeof s.playerId !== 'string' || s.playerId.length === 0) return null
    if (typeof s.deviceToken !== 'string' || s.deviceToken.length === 0) return null
    if (typeof s.name !== 'string' || s.name.length === 0) return null
    return {
      playerId: s.playerId,
      deviceToken: s.deviceToken,
      name: s.name,
      avatar: typeof s.avatar === 'string' ? s.avatar : null,
      ageBand: typeof s.ageBand === 'string' ? s.ageBand : 'BAND_4_5',
      classId: typeof s.classId === 'string' ? s.classId : '',
      className: typeof s.className === 'string' ? s.className : '',
      /*
       * ⚠️ DEFAULTED RATHER THAN REQUIRED, unlike the three fields above. A
       * tablet that signed a child in before this field existed holds a cached
       * session without it, and treating that as corrupt would sign every child
       * in the pilot out on the morning this ships — to fix a label.
       */
      schoolName: typeof s.schoolName === 'string' ? s.schoolName : '',
    }
  } catch {
    return null
  }
}

/** Called by the sign-in screen with what `POST /auth/kid` returned. */
export function setKidSession(session: KidSession): void {
  try {
    // Written as ONE value: a half-cache is worse than no cache (see above).
    storage?.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    /*
     * Non-fatal. This tab still works — the caller holds the same object we
     * just failed to write — and failing here would turn a storage quirk into a
     * sign-in that cannot complete, in front of a child.
     */
  }
}

/**
 * Sign out.
 *
 * ⚠️ LOCAL ONLY, AND THE TOKEN STAYS VALID SERVER-SIDE FOR ITS FULL TTL. There
 * is no kid logout route, deliberately: revocation for a device token is S2's
 * `DELETE /devices/:id`, which is an ADMIN act on a piece of school property.
 * A child tapping "not me" must not be able to revoke the tablet the next class
 * is about to use. 📋 The gap that remains — a child who signs out on a shared
 * laptop leaves a live token in nobody's hands — is bounded by the token naming
 * exactly one player and carrying no personal data (ADR-004).
 */
export function clearKidSession(): void {
  try {
    storage?.removeItem(SESSION_KEY)
  } catch {
    // Nothing useful to do; the next read will still parse whatever is there.
  }
}
